// Page-level error boundary. Catches render-time errors so a crash in
// one component doesn't blank the whole app. Shows a fallback UI with
// the error message + a "Try again" button that resets the boundary.

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button.js';
import { Card } from './ui/card.js';

interface Props {
  children: ReactNode;
  /** Optional fallback renderer. If omitted, a default UI is shown. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.reset);
      }
      // Bound the rendered message: a single-line (truncated) summary plus
      // the full text behind a collapsible <details>, capped + scrollable.
      // A raw multi-KB error.message (e.g. a verbose API error) must never
      // become a full-page "wall of red text". `min-w-0` lets `truncate`
      // actually clip inside the flex row.
      const message =
        this.state.error.message || 'An unexpected error occurred.';
      return (
        <Card className="border-destructive p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-destructive">
                Something went wrong
              </h2>
              <p className="mt-1 truncate text-sm text-muted-foreground">
                {message}
              </p>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                  Technical details
                </summary>
                <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs text-muted-foreground">
                  {message}
                </pre>
              </details>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" size="sm" onClick={this.reset}>
                  Try again
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  Reload page
                </Button>
              </div>
            </div>
          </div>
        </Card>
      );
    }
    return this.props.children;
  }
}
